/**
 * Skeleton - Base skeleton loading placeholder with shimmer animation
 * Used to show content shape while data is loading
 */

/**
 * Base skeleton element with shimmer effect
 */
export function Skeleton({ 
  width, 
  height, 
  className = '',
  rounded = 'sm',
  animate = true,
}) {
  const roundedClass = {
    none: '',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full',
  }[rounded] || 'rounded-sm';

  return (
    <div
      className={`skeleton ${roundedClass} ${animate ? 'skeleton-shimmer' : ''} ${className}`}
      style={{
        width: width || '100%',
        height: height || '1rem',
      }}
    />
  );
}

/**
 * Text skeleton - simulates a line of text
 */
export function SkeletonText({ lines = 1, className = '' }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton 
          key={i} 
          height="0.875rem" 
          width={i === lines - 1 && lines > 1 ? '75%' : '100%'} 
        />
      ))}
    </div>
  );
}

/**
 * Circle skeleton - for avatars, icons
 */
export function SkeletonCircle({ size = 40 }) {
  return <Skeleton width={size} height={size} rounded="full" />;
}

/**
 * Card skeleton - full card placeholder
 */
export function SkeletonCard({ className = '' }) {
  return (
    <div 
      className={`card ${className}`}
      style={{ backgroundColor: 'var(--color-bg-canvas)' }}
    >
      <div className="card-header">
        <Skeleton width="40%" height="1.25rem" />
      </div>
      <div className="card-body space-y-3">
        <SkeletonText lines={2} />
        <Skeleton width="60%" height="2rem" />
      </div>
    </div>
  );
}

/**
 * Table row skeleton
 */
export function SkeletonTableRow({ columns = 5 }) {
  return (
    <tr>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton 
            width={i === 0 ? '80%' : i === columns - 1 ? '60px' : '70%'} 
            height="1rem" 
          />
        </td>
      ))}
    </tr>
  );
}

/**
 * Calendar day skeleton
 */
export function SkeletonCalendarDay({ hasEvents = false }) {
  return (
    <div className="p-1 min-h-[80px] md:min-h-[100px]">
      <Skeleton width="1.5rem" height="1.5rem" className="mb-1" />
      {hasEvents && (
        <div className="space-y-1">
          <Skeleton height="1.25rem" rounded="md" />
          <Skeleton height="1.25rem" width="80%" rounded="md" />
        </div>
      )}
    </div>
  );
}

/**
 * Event pill skeleton
 */
export function SkeletonEventPill() {
  return (
    <div 
      className="px-2 py-1 rounded-md mb-1"
      style={{ backgroundColor: 'var(--color-bg-raised)' }}
    >
      <Skeleton height="0.75rem" width="85%" />
    </div>
  );
}

/**
 * Mobile card skeleton for expenses
 */
export function SkeletonMobileCard() {
  return (
    <div 
      className="p-4 rounded-lg"
      style={{ 
        backgroundColor: 'var(--color-bg-canvas)',
        border: '1px solid var(--color-border-subtle)',
      }}
    >
      <div className="flex items-start gap-3">
        <SkeletonCircle size={36} />
        <div className="flex-1 space-y-2">
          <Skeleton width="60%" height="1rem" />
          <Skeleton width="40%" height="0.75rem" />
        </div>
        <Skeleton width="70px" height="1.25rem" />
      </div>
    </div>
  );
}

/**
 * Budget category row skeleton
 */
export function SkeletonBudgetRow() {
  return (
    <div 
      className="p-4 rounded-lg"
      style={{ 
        backgroundColor: 'var(--color-bg-canvas)',
        border: '1px solid var(--color-border-subtle)',
      }}
    >
      <div className="flex items-center gap-3 mb-3">
        <SkeletonCircle size={32} />
        <Skeleton width="30%" height="1rem" />
        <div className="ml-auto">
          <Skeleton width="80px" height="1.25rem" />
        </div>
      </div>
      <Skeleton height="8px" rounded="full" className="mb-2" />
      <div className="flex justify-between">
        <Skeleton width="25%" height="0.75rem" />
        <Skeleton width="25%" height="0.75rem" />
      </div>
    </div>
  );
}

export default Skeleton;
